/**
 * Our own trolley scene — inline SVG, no external assets. Two tracks, a lever,
 * and a switch that animates to whichever side the player chose. Purely
 * decorative: the dilemma text carries the meaning.
 */
interface Props {
  /** null = undecided; "a" = straight on, "b" = diverted. */
  pick: "a" | "b" | null;
}

export function TrolleyScene({ pick }: Props) {
  const diverted = pick === "b";
  return (
    <svg viewBox="0 0 320 130" className="mx-auto w-full max-w-[420px]" role="img" aria-label="вагонетка на розвилці">
      <title>вагонетка на розвилці</title>
      {/* ground */}
      <rect x="0" y="0" width="320" height="130" fill="none" />
      {/* main track (straight, option A) */}
      <g stroke="currentColor" strokeOpacity="0.35" strokeWidth="2" fill="none">
        <line x1="10" y1="86" x2="310" y2="86" />
        <line x1="10" y1="94" x2="310" y2="94" />
        {Array.from({ length: 15 }, (_, i) => (
          <line key={`s${i}`} x1={14 + i * 20} y1="82" x2={14 + i * 20} y2="98" strokeWidth="3" strokeOpacity="0.22" />
        ))}
      </g>
      {/* branch track (option B) */}
      <g stroke="currentColor" strokeOpacity="0.35" strokeWidth="2" fill="none">
        <path d="M150 86 C 200 86, 230 50, 310 44" />
        <path d="M150 94 C 200 94, 232 58, 310 52" />
      </g>

      {/* switch: highlights the chosen route */}
      <path
        d={diverted ? "M150 90 C 200 90, 232 54, 300 48" : "M150 90 L 300 90"}
        stroke="var(--tg-theme-button-color, #2ea6ff)"
        strokeWidth={pick ? 4 : 0}
        fill="none"
        strokeLinecap="round"
        opacity={pick ? 0.9 : 0}
        style={{ transition: "opacity .25s" }}
      />

      {/* people on the straight track (A) */}
      <g opacity={pick && !diverted ? 0.35 : 1} style={{ transition: "opacity .3s" }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Person key={`a${i}`} x={236 + i * 13} y={78} />
        ))}
      </g>
      {/* person on the branch (B) */}
      <g opacity={diverted ? 0.35 : 1} style={{ transition: "opacity .3s" }}>
        <Person x={272} y={36} />
      </g>

      {/* the lever operator — you */}
      <g transform="translate(126 62)">
        <Person x={0} y={16} />
        <line
          x1="9"
          y1="20"
          x2={diverted ? 20 : 15}
          y2={diverted ? 8 : 2}
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{ transition: "all .25s" }}
        />
      </g>

      {/* trolley */}
      <g transform="translate(40 62)">
        <rect x="0" y="0" width="42" height="22" rx="4" fill="var(--tg-theme-button-color, #2ea6ff)" opacity="0.85" />
        <rect x="6" y="4" width="12" height="9" rx="2" fill="#fff" opacity="0.75" />
        <rect x="23" y="4" width="12" height="9" rx="2" fill="#fff" opacity="0.75" />
        <circle cx="10" cy="26" r="5" fill="currentColor" opacity="0.6" />
        <circle cx="32" cy="26" r="5" fill="currentColor" opacity="0.6" />
      </g>
    </svg>
  );
}

function Person({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none">
      <circle cx="4" cy="2" r="2.6" fill="currentColor" stroke="none" />
      <line x1="4" y1="5" x2="4" y2="11" />
      <line x1="4" y1="7" x2="1" y2="9.5" />
      <line x1="4" y1="7" x2="7" y2="9.5" />
      <line x1="4" y1="11" x2="1.5" y2="15" />
      <line x1="4" y1="11" x2="6.5" y2="15" />
    </g>
  );
}
